import { z } from "zod/v4-mini";

import { FileModel, FileToUpload, fileSchema } from "@/models/FileModel.ts";
import { Profession, professionSchema } from "@/models/ProfessionModel.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

export type ChatVisibilityType = "all" | "practitioners";

export type ReadReceipt = {
  userId: string;
  userType: string;
  firstname: string;
  lastname: string;
  qualification?: Profession;
  profilePicture?: FileModel;
  messageId: string;
  read: boolean;
  readAt?: string;
};

export type Reaction = {
  userId: string;
  userType: string;
  firstname: string;
  lastname: string;
  qualification?: Profession;
  profilePicture?: FileModel;
  messageId: string;
  emoji: string;
  createdAt: string;
};

export type GroupedReaction = {
  emoji: string;
  count: number;
  reactedByMe: boolean;
};

export type Message = {
  id: string;

  userId: string;
  userType: string;
  firstname: string;
  lastname: string;
  qualification?: Profession;
  profilePicture?: FileModel;
  subjectId: string;

  visibility: ChatVisibilityType;
  text?: string;
  deleted: boolean;
  deletedAt?: string;
  readReceipts: ReadReceipt[];
  reactions: Reaction[];
  attachments: { url: string; name: string }[];

  createdAt: string;
};

export type UnreadMessageCount = {
  fromPractitioner: number;
  fromPatient: number;
};

const userSchema = cz.transform(
  z.discriminatedUnion("user_type", [
    cz.camelCaseObject({
      user_type: z.literal("patient"),
      patient: z.object({
        id: z.string(),
        firstname: z.string(),
        lastname: z.string(),
      }),
    }),
    cz.camelCaseObject({
      user_type: z.literal("practitioner"),
      practitioner: cz.camelCaseObject({
        id: z.string(),
        firstname: z.string(),
        lastname: z.string(),
        qualification: professionSchema,
        profile_picture: cz.optional(fileSchema),
      }),
    }),
  ]),
  data =>
    data.userType === "patient"
      ? {
          userType: "patient",
          userId: data.patient.id,
          firstname: data.patient.firstname,
          lastname: data.patient.lastname,
          qualification: undefined,
          profilePicture: undefined,
        }
      : {
          userType: "practitioner",
          userId: data.practitioner.id,
          firstname: data.practitioner.firstname,
          lastname: data.practitioner.lastname,
          qualification: data.practitioner.qualification,
          profilePicture: data.practitioner.profilePicture,
        },
);

const readReceiptSchema: Schema<ReadReceipt> = z.intersection(
  userSchema,
  cz.transform(
    cz.camelCaseObject({
      message_id: z.string(),
      read: z.boolean(),
      read_date: cz.optional(cz.datetime()),
    }),
    data => ({
      messageId: data.messageId,
      read: data.read,
      readAt: data.readDate,
    }),
  ),
);

const reactionSchema: Schema<Reaction> = z.intersection(
  userSchema,
  cz.transform(
    cz.camelCaseObject({
      message_id: z.string(),
      emoji: z.string(),
      created_at: cz.datetime(),
    }),
    data => ({
      messageId: data.messageId,
      emoji: data.emoji,
      createdAt: data.createdAt,
    }),
  ),
);

export const messageSchema: Schema<Message> = z.intersection(
  userSchema,
  cz.transform(
    cz.camelCaseObject({
      id: z.string(),
      subject_id: z.string(),

      visibility: z.literal(["all", "practitioners"]),
      content_text: cz.optional(z.string()),
      deleted: z.boolean(),
      deleted_at: cz.optional(cz.datetime()),
      read_receipts: cz.forcedArray(readReceiptSchema),
      reactions: cz.forcedArray(reactionSchema),
      attachments: cz.forcedArray(
        z.object({
          url: z.string(),
          name: z.string(),
        }),
      ),
      created_at: cz.datetime(),
    }),
    data => ({
      id: data.id,
      subjectId: data.subjectId,

      visibility: data.visibility,
      text: data.contentText,
      deleted: data.deleted,
      deletedAt: data.deletedAt,
      readReceipts: data.readReceipts,
      reactions: data.reactions,
      attachments: data.attachments,

      createdAt: data.createdAt,
    }),
  ),
);

export const unreadMessageCountSchema: Schema<UnreadMessageCount> = cz.camelCaseObject({
  from_patient: z.number(),
  from_practitioner: z.number(),
});

export type CreateMessage = {
  visibility: ChatVisibilityType;
  content_text?: string;
  attachments?: FileToUpload[];
};

export const isMessageRead = (message: Message, practitionerId: string) => {
  if (message.deleted || isMessageSender(message, practitionerId)) {
    return true;
  }
  const receipt = message.readReceipts.find(r => r.userId === practitionerId);
  if (!receipt) {
    return true;
  }
  return receipt.read;
};

export const isMessageSender = (message: Message, practitionerId: string) => message.userId === practitionerId;

export const groupReactions = (reactions: Reaction[], meId: string): GroupedReaction[] => {
  const byEmoji = new Map<string, GroupedReaction>();
  for (const reaction of reactions) {
    const group = byEmoji.get(reaction.emoji) ?? { emoji: reaction.emoji, count: 0, reactedByMe: false };
    group.count += 1;
    group.reactedByMe = group.reactedByMe || reaction.userId === meId;
    byEmoji.set(reaction.emoji, group);
  }
  return [...byEmoji.values()];
};

export const myReaction = (reactions: Reaction[], meId: string): string | undefined =>
  reactions.find(reaction => reaction.userId === meId)?.emoji;
